import { createStore } from "@stencil/store";
import { getCheckedCheckboxesValues } from "./getCheckedCheckboxesValues";

interface StaticFilterOptions {
    counterSelector: string;
    filterElement: HTMLUxFilterElement;
    itemsContainerElement: HTMLElement;
    itemFilterAttribute: string;
    itemHiddenAttribute: string;
}

/**
 * Creates the filter functionality for a static list of items. All the filtering is done in the DOM.
 * @param options
 * @param options.counterSelector the css selector for the element that will show the number of visible items
 * @param options.filterElement the ux-filter element that will be used to filter the items
 * @param options.itemsContainerElement the element that contains the items to be filtered
 * @param options.itemFilterAttribute the attribute on the items that hold their filter values
 * @param options.itemHiddenAttribute the attribute that will be set on the items that are hidden
 * @returns an object with the store and an update function
 */
export const createStaticFilter = ({
    counterSelector,
    filterElement,
    itemFilterAttribute,
    itemsContainerElement,
    itemHiddenAttribute,
}: StaticFilterOptions) => {
    const allCheckboxes: HTMLUxCheckboxListItemElement[] = Array.from(
        filterElement.querySelectorAll("ux-checkbox-list-item")
    );
    const store = createStore({
        visibleItemsCount: 0,
        selectedFilters: [],
        availableFilters: [],
    });

    filterElement.addEventListener("uxFilterChange", async () => {
        const checkedCheckboxesValues =
            await getCheckedCheckboxesValues(allCheckboxes);
        store.set("selectedFilters", [...checkedCheckboxesValues]);
    });

    store.onChange("selectedFilters", (selectedFilters) => {
        const items = Array.from(
            itemsContainerElement.querySelectorAll(`*[${itemFilterAttribute}]`)
        );
        let visibleItemsCount = 0;
        items.forEach((item: HTMLElement) => {
            if (selectedFilters.length === 0) {
                item.removeAttribute(itemHiddenAttribute);
                visibleItemsCount++;
            } else {
                const teaserFilterAttributes = item
                    .getAttribute(itemFilterAttribute)
                    .split(",");
                const isSelected = selectedFilters.every((filter: string) =>
                    teaserFilterAttributes.includes(filter)
                );
                if (isSelected) {
                    item.removeAttribute(itemHiddenAttribute);
                    visibleItemsCount++;
                } else {
                    item.setAttribute(itemHiddenAttribute, "");
                }
            }
        });
        store.set("visibleItemsCount", visibleItemsCount);
        updateAvailableFilters();
    });

    const updateAvailableFilters = () => {
        const visibleItems = Array.from(
            itemsContainerElement.querySelectorAll(
                `*[${itemFilterAttribute}]:not([${itemHiddenAttribute}])`
            )
        );
        const availableFilters = [];
        visibleItems.forEach((item: HTMLElement) => {
            const filtersValues = item
                .getAttribute(itemFilterAttribute)
                .split(",");
            filtersValues.forEach((filterValue: string) => {
                if (!availableFilters.includes(filterValue))
                    availableFilters.push(filterValue);
            });
        });
        store.set("availableFilters", availableFilters);
    };

    store.onChange("availableFilters", (availableFilters) => {
        allCheckboxes.forEach((item: HTMLUxCheckboxListItemElement) => {
            if (!availableFilters.includes(item.value)) {
                item.setAttribute("disabled", "true");
            } else {
                item.removeAttribute("disabled");
            }
        });
    });

    store.onChange("visibleItemsCount", (count) => {
        const counterElement = document.querySelector(
            counterSelector
        ) as HTMLElement;
        if (!counterElement) return;
        counterElement.innerText = count.toString();
    });

    /** update the filters when the DOM was changed externally */
    const update = () => {
        getCheckedCheckboxesValues(allCheckboxes).then((checkedValues) => {
            store.set("selectedFilters", [...checkedValues]);
        });
        updateAvailableFilters();
    };
    update();

    return { store, update };
};
